/**
 * Adapter for the Regulatory section's Survey tab.
 *
 * The wire `/v2/surveys/` returns one row *per survey date* with only
 * `facility_id` to identify the facility. The Survey table renders one
 * row per facility with per-year buckets (`years: Record<year, …>`).
 * This module is the single bridge.
 *
 * Surveys carry only `facility_id`, so the page joins against the
 * submission's Schedule locations (the SoT) and passes a
 * `facility_id → { name, state }` map (`refs`) to fill the Facility Name
 * / State columns. An unresolved id (or no refs) renders an em-dash. If
 * the surveys list is empty the page shows the Empty State; nothing in
 * this adapter has to know about that — it just returns `[]`.
 *
 * Aggregation rules:
 *   • Group surveys by `facility_id` + `cms_number` concatenated (Henry,
 *     jun-19): one of the two is normally present, so null-`facility_id`
 *     rows are NO LONGER dropped — they join to the Schedule locations by
 *     `cms_number` instead. Rows with BOTH null collapse into a single
 *     "empty facility" group.
 *   • Bucket each survey by `year(survey_date)`. Surveys with a null /
 *     unparseable `survey_date` are dropped from the year buckets, but
 *     their `num_fines` / `total_incurred_fines` still count toward the
 *     row-level lifetime totals.
 *   • Per-year metrics use the MOST RECENT survey in that bucket
 *     (max `survey_date`). Matches the "Last Survey" column label.
 *   • `fineCount` and `totalFines` are SUMMED across ALL surveys of
 *     the facility (every year), so the column reflects lifetime
 *     activity.
 *   • A facility row is `readOnly` when EVERY underlying wire row is
 *     `survey_source = "cms"`. One orion-authored row makes the whole
 *     facility row editable (delete will iterate over the wire ids).
 */
import type { SurveyGetWire } from "../../types/survey";
import type { OccupancyListItemWire } from "../endpoints/occupancies";
import type { AcuityBedField, AcuityRow, SurveyFacilityRef, SurveyRow, YearMetrics } from "../../pages/submission/regulatory/types";

const EMPTY_DASH = "—";

/** Group key for facility records. `facility_id` alone identifies the
 *  facility whenever it's present; `cms_number` is only the fallback for
 *  rows the backend couldn't resolve to a facility (Henry, jun-19 — kept so
 *  those still render). Keying on the facility_id + cms PAIR split a
 *  facility's rows in two when some records carried a cms and others didn't
 *  (the occupancy POST doesn't copy the facility's cms_number — OR-1152),
 *  which rendered a just-added Acuity year as a separate duplicate row. */
function facilityKey(facilityId: string | null | undefined, cms: string | null | undefined): string {
    if (facilityId) return `${facilityId}|`;
    return `|${cms ?? ""}`;
}

/** Parse the four-digit year from an ISO `YYYY-MM-DD`. Returns `null`
 *  when the input is missing or doesn't match the expected shape. */
function yearOf(iso: string | null): string | null {
    if (!iso) return null;
    const match = /^(\d{4})-\d{2}-\d{2}$/.exec(iso);
    return match ? match[1] : null;
}

/** Parse an ISO `YYYY-MM-DD` to a numeric millis for comparisons.
 *  Returns `0` (i.e. "always older") when the input is missing /
 *  malformed so the comparison stays total. */
function toMillis(iso: string | null): number {
    if (!iso) return 0;
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    if (!match) return 0;
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])).getTime();
}

/** "2023-03-15" → "03/15/2023". Empty string for missing input — the
 *  table renders the empty year buckets as blank cells. */
function formatSurveyDate(iso: string | null): string {
    if (!iso) return "";
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    if (!match) return "";
    const [, y, m, d] = match;
    return `${m}/${d}/${y}`;
}

/** Sum decimal-string fines into a single USD-formatted string. Falls
 *  back to "$0.00" when no row has a numeric value. */
function formatTotalFines(values: ReadonlyArray<string | null>): string {
    let total = 0;
    for (const v of values) {
        if (!v) continue;
        const n = Number.parseFloat(v);
        if (Number.isFinite(n)) total += n;
    }
    return total.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
}

/** Build an empty `YearMetrics` for years where the facility had no
 *  survey landed — the table renders the cells as "—" (null metrics). */
function emptyYearMetrics(): YearMetrics {
    return {
        lastSurvey: "",
        severityCitations: null,
        citationCount: null,
        complaintCount: null,
        explanation: "",
    };
}

function toYearMetrics(survey: SurveyGetWire): YearMetrics {
    return {
        lastSurvey: formatSurveyDate(survey.survey_date),
        // Keep null (→ "—") distinct from a real backend 0 (→ "0").
        severityCitations: survey.num_severity_citations,
        citationCount: survey.num_citations,
        complaintCount: survey.num_complaint_surveys,
        explanation: survey.explanation ?? "",
        wireId: survey.id,
    };
}

/**
 * Collapse wire surveys into one `SurveyRow` per facility, with one
 * `YearMetrics` per year in `years`. See file header for the aggregation
 * rules. The output order follows first-seen `facility_id` from the
 * surveys list.
 *
 * @param wire   Raw rows from `GET /v2/surveys/?submission_id=…`.
 * @param years  Year buckets the table renders, e.g. ["2023","2022",…].
 * @param refs   `facility_id → { name, state }` resolved from the
 *               submission's Schedule locations (the SoT). Surveys carry
 *               only `facility_id`; this fills the name/state columns.
 *               Unresolved ids fall back to the em-dash.
 */
export function aggregateSurveys(
    wire: readonly SurveyGetWire[],
    years: readonly string[],
    refs?: ReadonlyMap<string, SurveyFacilityRef>,
    refsByCms?: ReadonlyMap<string, SurveyFacilityRef>,
): SurveyRow[] {
    // Group wire rows by facility_id + cms_number (Henry, jun-19). Rows are
    // no longer dropped when facility_id is null — they group by cms_number
    // and resolve their name/state from `refsByCms`. Both-null rows fall
    // into a single empty group.
    const byKey = new Map<string, SurveyGetWire[]>();
    for (const row of wire) {
        const key = facilityKey(row.facility_id, row.cms_number);
        const bucket = byKey.get(key);
        if (bucket) bucket.push(row);
        else byKey.set(key, [row]);
    }

    const rows: SurveyRow[] = [];
    let idx = 1;
    for (const [key, surveys] of byKey) {
        // Representative ids for the group (first non-null of each).
        const facilityId = surveys.find((s) => s.facility_id)?.facility_id ?? "";
        const cmsNumber = surveys.find((s) => s.cms_number)?.cms_number ?? "";
        // Initialize empty year buckets so the table renders all
        // configured years even when the facility skipped one.
        const yearsMap: Record<string, YearMetrics> = {};
        for (const y of years) yearsMap[y] = emptyYearMetrics();

        // For each year, keep only the most-recent survey.
        const mostRecentPerYear = new Map<string, SurveyGetWire>();
        for (const s of surveys) {
            const yr = yearOf(s.survey_date);
            if (!yr || !years.includes(yr)) continue;
            const current = mostRecentPerYear.get(yr);
            if (!current || toMillis(s.survey_date) > toMillis(current.survey_date)) {
                mostRecentPerYear.set(yr, s);
            }
        }
        for (const [yr, s] of mostRecentPerYear) yearsMap[yr] = toYearMetrics(s);

        // Resolve name/state from the submission's Schedule locations (the
        // SoT) — by facility_id when present, else by cms_number (stage
        // locations carry cms_number even when facility_id is null).
        // Em-dash when unresolved.
        const ref =
            (facilityId ? refs?.get(facilityId) : undefined) ??
            (cmsNumber ? refsByCms?.get(cmsNumber) : undefined);
        rows.push({
            id: key,
            index: idx++,
            facilityId,
            facilityName: ref?.name || EMPTY_DASH,
            state: ref?.state || EMPTY_DASH,
            fineCount: surveys.reduce((acc, s) => acc + (s.num_fines ?? 0), 0),
            totalFines: formatTotalFines(surveys.map((s) => s.total_incurred_fines)),
            years: yearsMap,
            readOnly: surveys.every((s) => s.survey_source === "cms"),
            wireIds: surveys.map((s) => s.id),
        });
    }

    return rows;
}

// ─── Acuity tab — occupancies ───────────────────────────────────────────────

/** Care-type → backend bed-count column, in Figma order. Exported so the
 *  Acuity table renders its expandable care-type sub-rows from the same
 *  registry that shapes the census maps here. */
export const ACUITY_CARE_TYPES: { label: string; field: AcuityBedField }[] = [
    { label: "Skilled", field: "skilled_beds" },
    { label: "Assisted", field: "assisted_beds" },
    { label: "Independent", field: "independent_beds" },
    { label: "Add'l. Exposure", field: "additional_beds" },
];

/**
 * Turn `/v2/occupancies/?submission_id=…` rows into Acuity table rows.
 *
 * The backend returns one row per (facility, year) with bed counts split
 * by care type. The Acuity table (OR-1328) shows ONE expandable row per
 * facility: collapsed it renders the "All" sum per year; expanded it adds
 * one sub-row per care type. So each `AcuityRow` carries the full
 * care-type × year census grid (`census[field][year]`), with `""` for
 * cells the backend has no value for (→ rendered as a dash, distinct
 * from a confirmed `0`).
 *
 * @param wire   Raw rows from the occupancies list.
 * @param years  Year columns the table renders, e.g. ["2026","2023",…].
 */
export function aggregateOccupancies(
    wire: readonly OccupancyListItemWire[],
    years: readonly string[],
): AcuityRow[] {
    // One wire row per facility+year — group by facility_id + cms_number
    // (Henry, jun-19), so null-facility rows still render instead of being
    // dropped. Both-null rows collapse into a single empty group.
    const byKey = new Map<string, OccupancyListItemWire[]>();
    for (const row of wire) {
        const key = facilityKey(row.facility_id, row.cms_number);
        const bucket = byKey.get(key);
        if (bucket) bucket.push(row);
        else byKey.set(key, [row]);
    }

    const rows: AcuityRow[] = [];
    let idx = 1;
    for (const [key, items] of byKey) {
        const first = items[0];
        // Map each rendered year → the backing occupancy record id so the
        // table can PATCH edits (and know whether one exists to create).
        const occupancyByYear: Record<string, string> = {};
        // ALL record ids per year — occupancyByYear keeps one id per year
        // (last wins), but Delete Column must remove duplicated-year
        // records too (same resurrection hazard as the facility trash,
        // OR-1152).
        const occupancyIdsByYear: Record<string, string[]> = {};
        for (const it of items) {
            const y = String(it.year);
            occupancyByYear[y] = it.id;
            (occupancyIdsByYear[y] ??= []).push(it.id);
        }
        // Every backing record id — for the facility-level trash flow.
        const wireIds = items.map((it) => it.id);
        const facilityId = first.facility_id ?? "";
        // Full care-type × year census grid as editable strings — "" when
        // the facility has no value for that (type, year).
        const census = {} as AcuityRow["census"];
        for (const careType of ACUITY_CARE_TYPES) {
            const perYear: Record<string, string> = {};
            for (const y of years) perYear[y] = "";
            for (const it of items) {
                const y = String(it.year);
                if (!(y in perYear)) continue;
                const n = it[careType.field];
                if (n != null) perYear[y] = String(n);
            }
            census[careType.field] = perYear;
        }
        rows.push({
            id: key,
            index: idx++,
            facilityName: first.facility_name || first.cms_number || "",
            state: first.state ?? "",
            census,
            facilityKey: key,
            facilityId,
            occupancyByYear,
            occupancyIdsByYear,
            wireIds,
        });
    }
    return rows;
}
